/*
 * Regexp to match a single HTML tag.
 */
const tagPattern = /^<(\w+)>$/;

/*
 * Get or create a single element. If the query is in the form of a tag, e.g.
 * '<p>' it creates an element of that type. Otherwise it queries the document
 * using the argument as a selector.
 */
interface Dollar {
  <K extends keyof HTMLElementTagNameMap>(
    q: `<${K}>`,
    content?: string | Node,
  ): HTMLElementTagNameMap[K];
  <E extends Element = HTMLElement>(q: string, content?: string | Node): E;
}

const $ = ((q: string, content?: string | Node) => {
  const tag = q.match(tagPattern)?.[1];
  if (tag) {
    const e = document.createElement(tag);
    if (content !== undefined) {
      e.append(content);
    }
    return e;
  } else {
    return document.querySelector(q);
  }
}) as Dollar;

/*
 * Get all elements matching selector.
 */
const $$ = <E extends Element = HTMLElement>(q: string): NodeListOf<E> =>
  document.querySelectorAll(q) as NodeListOf<E>;

/*
 * Create a text node.
 */
const text = (t: string): Text => document.createTextNode(t);

/*
 * Get an object containing all DOM objects with an id.
 */
const byId = (): Record<string, HTMLElement> =>
  Object.fromEntries([...document.querySelectorAll<HTMLElement>('[id]')].map((e) => [e.id, e]));

/*
 * Decorate a DOM element with a CSS class.
 */
const withClass = <E extends Element>(className: string, e: E): E => {
  e.className = className;
  return e;
};

/*
 * Make an A element with a give text and href and an optional target.
 */
const a = (text: string | Node, href: string, target?: string): HTMLAnchorElement => {
  const e = $('<a>', text);
  e.setAttribute('href', href);
  if (target) {
    e.setAttribute('target', target);
  }
  return e;
};

/*
 * Sprite locations for the two icon sets: Bootstrap Icons ('bi') and our
 * own BHS Icons ('bhi', the bhs-icons workspace).
 */
const SPRITES = {
  bi: '/img/bootstrap-icons.svg',
  bhi: '/bhs-icons/bhs-icons.svg',
};

/*
 * Make a named icon, by default a Bootstrap one.
 */
const icon = (name: string, set: keyof typeof SPRITES = 'bi'): SVGSVGElement => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  svg.classList.add(set);
  use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `${SPRITES[set]}#${name}`);
  svg.append(use);
  return svg;
};

/*
 * Narrow away null/undefined where the value is invariantly present (e.g. an
 * element the page template always renders), failing loudly rather than with
 * a downstream TypeError if the invariant is broken.
 */
const must = <T>(value: T | null | undefined, what: string): T => {
  if (value == null) throw new Error(`Expected ${what}`);
  return value;
};

export { $, $$, a, byId, icon, must, text, withClass };
